import { RcFile, UploadFile } from "antd/es/upload";

import { PhotoCaptions } from "@/types/common";

export interface UploadImageFile extends UploadFile {
    previewUrl?: string;
    _fingerprint?: string;
}

export const fingerprintOf = (file: UploadFile): string => {
    const orig = file.originFileObj as RcFile | undefined;
    const name = file.name ?? orig?.name ?? "";
    const size = (file.size ?? orig?.size ?? 0).toString();
    const lm = (orig?.lastModified ?? 0).toString();
    const type = file.type ?? orig?.type ?? "";
    return [name, size, lm, type].join("__");
};

export const readAsDataURL = (file: RcFile) =>
    new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

export const ensurePreviewDataUrl = async (file: UploadImageFile) => {
    if (file.previewUrl) return file.previewUrl;
    const orig = file.originFileObj as RcFile | undefined;
    if (!orig) {
        if (file.thumbUrl) {
            file.previewUrl = file.thumbUrl;
            return file.previewUrl;
        }
        throw new Error("No origin file to create preview");
    }
    file.previewUrl = await readAsDataURL(orig);
    return file.previewUrl;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
): string[] => {
    if (!text) return [];
    const words = text.split(" ");
    const lines: string[] = [];
    let current = "";

    for (const word of words) {
        const test = current ? `${current} ${word}` : word;
        if (ctx.measureText(test).width <= maxWidth) {
            current = test;
        } else {
            if (current) lines.push(current);
            current = word;
        }
    }
    if (current) lines.push(current);
    return lines;
};

/**
 * Loads a File/Blob into an HTMLImageElement via a temporary object URL.
 * Works on all iOS Safari versions (unlike createImageBitmap which is unreliable).
 */
const loadImageFromFile = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = (err) => {
            URL.revokeObjectURL(url);
            reject(err);
        };
        img.src = url;
    });

/**
 * Loads a logo from a remote URL.
 * Falls back gracefully if the image is tainted or fails to load.
 */
const loadImageFromUrl = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = new Image();
        // Set crossOrigin BEFORE setting src to avoid canvas taint on iOS.
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

/**
 * canvas.toBlob wrapper that:
 * - always returns a non-null Blob (throws instead)
 * - has an explicit timeout so we don't hang forever on iOS low-memory
 */
const toBlobSafe = (
    canvas: HTMLCanvasElement,
    type = "image/jpeg",
    quality = 0.92,
    timeoutMs = 15_000,
): Promise<Blob> =>
    new Promise((resolve, reject) => {
        const timer = setTimeout(
            () => reject(new Error("toBlob timed out")),
            timeoutMs,
        );

        try {
            canvas.toBlob(
                (blob) => {
                    clearTimeout(timer);
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error("toBlob returned null"));
                    }
                },
                type,
                quality,
            );
        } catch (err) {
            clearTimeout(timer);
            reject(err);
        }
    });

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export const addCaptionBR = async (
    file: File,
    captions?: PhotoCaptions,
    logoUrl?: string,
): Promise<Blob> => {
    // Use HTMLImageElement instead of createImageBitmap — safer on iOS Safari.
    const img = await loadImageFromFile(file);

    const measureCanvas = document.createElement("canvas");
    // getContext can return null — guard it.
    const measureCtx = measureCanvas.getContext("2d");
    if (!measureCtx) throw new Error("Could not get 2d context for measuring");

    const base = Math.max(img.naturalWidth, img.naturalHeight);

    const fontSize = Math.round(base * 0.02);
    const lineHeight = Math.round(fontSize * 1.2);
    const padding = Math.round(base * 0.015);
    const maxWidth = img.naturalWidth - padding * 2;

    measureCtx.font = `bold ${fontSize}px Arial, sans-serif`;

    const bottomWrappedLine0 = wrapText(
        measureCtx,
        captions?.bottomLines?.[0] ?? "",
        maxWidth,
    );
    const bottomLine1 = captions?.bottomLines?.[1] ?? "";
    const totalBottomLines = bottomWrappedLine0.length + (bottomLine1 ? 1 : 0);

    const topLines = captions?.topLines ?? [];
    const totalTopLines = Math.max(topLines.length, 1);

    const topBarHeight =
        totalTopLines > 0 ? padding * 2 + totalTopLines * lineHeight : 0;
    const bottomBarHeight =
        totalBottomLines > 0 ? padding * 2 + totalBottomLines * lineHeight : 0;

    const canvas = document.createElement("canvas");
    // Guard getContext here too.
    const ctx = canvas.getContext("2d", { willReadFrequently: false });
    if (!ctx) throw new Error("Could not get 2d context for drawing");

    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight + topBarHeight + bottomBarHeight;

    // Top black bar
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, canvas.width, topBarHeight);

    // Image
    ctx.drawImage(img, 0, topBarHeight, img.naturalWidth, img.naturalHeight);

    // Bottom black bar
    ctx.fillStyle = "#000000";
    ctx.fillRect(
        0,
        topBarHeight + img.naturalHeight,
        canvas.width,
        bottomBarHeight,
    );

    // Logo in top bar (right-aligned)
    if (logoUrl) {
        try {
            const logo = await loadImageFromUrl(logoUrl);

            const logoHeight = Math.round(topBarHeight * 0.6);
            const logoWidth = Math.round(
                (logo.naturalWidth / logo.naturalHeight) * logoHeight,
            );
            const logoX = canvas.width - logoWidth - padding;
            const logoY = (topBarHeight - logoHeight) / 2;

            ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);
        } catch (err) {
            // Logo failure must not break the whole image.
            console.error("Error loading logo — skipping:", err);
        }
    }

    ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#FFFFFF";

    // Top bar text
    if (topLines.length > 0) {
        ctx.textAlign = "left";
        const blockHeight = totalTopLines * lineHeight;
        let y = (topBarHeight - blockHeight) / 2 + fontSize;

        for (const line of topLines) {
            ctx.fillText(line, padding, y);
            y += lineHeight;
        }
    }

    // Bottom bar text
    if (totalBottomLines > 0) {
        ctx.textAlign = "left";
        const bottomBarStart = topBarHeight + img.naturalHeight;
        let y = bottomBarStart + padding + fontSize;

        for (const line of bottomWrappedLine0) {
            ctx.fillText(line, padding, y);
            y += lineHeight;
        }

        if (bottomLine1) {
            ctx.fillText(bottomLine1, padding, y);
        }
    }

    return toBlobSafe(canvas);
};
